import { usePublicationEnricher } from "./usePublicationEnricher";

import {
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainHookProps,
    useDetailLifeCycle,
} from "@/Shared";

import { usePublication } from "@/OneLiners";
import { PublicationViewModel } from "../ViewModels";
import { PublicationModel } from "../Models";


/**
 * usePublicationDetail hook
 *
 * Domain-specific detail hook for Publication entities.
 * Orchestrates domain data enrichment, deletion workflow,
 * UI feedback (toasts, confirmation), and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the PublicationViewModel when data is available.
 * - Enrich the publication with associated documents.
 * - Handle delete operations with access control and confirmation.
 * - Handle PDF export for the current publication.
 * - Manage navigation (layout, error page) based on user permissions.
 * - Expose entity VM, action executor, and access rights to the UI layer.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainDetailHook<PublicationViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<PublicationViewModel>
 */
export function usePublicationDetail(props: DomainHookProps): DomainDetailHook<PublicationViewModel> {
    const { permissionHook, accessRight, uiFeedbackHooks } = props;
    const { domainType } = DOMAIN_CONFIGS.Publication;

    /**
     * Publication-related enums exposed to the UI layer
     */
    const { PublicationEnums, PropertyEnums } = useAppEnum();

    /**
     * Publication domain CRUD operations and state
     */
    const publicationHook = usePublication();

    /**
     * Publication domain enrichment facade
     */
    const { getStrictEnrichedPublication, enricherResponse, } = usePublicationEnricher();

    /**
     * Combined response acting as a single readiness contract
     * for initializing the detail view
     */
    const response = {
        status: ActionStatusHelper.combine(
            publicationHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: publicationHook.stateResponse.error ??
            enricherResponse.error
    }

    /**
         * Detail life cycle hook
         *
         * Handles:
         * - Entity resolution (queryId / entityId)
         * - ViewModel construction
         * - Post-fetch or post-update handling
         * - Provides public API to the UI layer
         */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: publicationHook,
        buildVM: (currentEntity: PublicationModel, isDisplaying: boolean) => {
            return new PublicationViewModel({
                publication: getStrictEnrichedPublication(currentEntity),
                viewEnums: PublicationEnums,
                isDisplaying,
                accessRight,
                propertyEnums: PropertyEnums
            })
        },
    });

    /**
     * Public API exposed to the UI layer.
     */
    return {
        ...lifeCycleHook
    }
}